import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProductionDeclarationMaterialSupplyChainDialog } from '@/features/production-declaration/hooks/use-production-declaration-material-supply-chain-dialog';

type ProductionDeclarationMaterialSupplyChainDialogProps = {
  commodityOriginId: string;
  materialId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionDeclarationId: string;
  token?: string;
};

export function ProductionDeclarationMaterialSupplyChainDialog({
  commodityOriginId,
  materialId,
  open,
  onOpenChange,
  productionDeclarationId,
  token,
}: ProductionDeclarationMaterialSupplyChainDialogProps) {
  const {
    actorTypes,
    handleOpenChange,
    handleSubmit,
    isLoadingActorTypes,
    isLoadingSubcontractors,
    isPending,
    selectedActorTypeId,
    selectedSubcontractorId,
    setSelectedActorTypeId,
    setSelectedSubcontractorId,
    subcontractors,
    t,
    tCommon,
  } = useProductionDeclarationMaterialSupplyChainDialog({
    commodityOriginId,
    materialId,
    onOpenChange,
    productionDeclarationId,
    token,
  });

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>
            {t('materialCommodityOrigins.cards.supplyChain.dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('materialCommodityOrigins.cards.supplyChain.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>
              {t(
                'materialCommodityOrigins.cards.supplyChain.dialog.subcontractorLabel',
              )}
            </Label>
            <Select
              disabled={isLoadingSubcontractors}
              onValueChange={setSelectedSubcontractorId}
              value={selectedSubcontractorId}
            >
              <SelectTrigger className='w-full'>
                <SelectValue
                  placeholder={t(
                    'materialCommodityOrigins.cards.supplyChain.dialog.placeholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {subcontractors.map((subcontractor) => (
                  <SelectItem
                    key={subcontractor.id}
                    value={String(subcontractor.id)}
                  >
                    {subcontractor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label>
              {t('materialCommodityOrigins.cards.supplyChain.dialog.roleLabel')}
            </Label>
            <Select
              disabled={isLoadingActorTypes}
              onValueChange={setSelectedActorTypeId}
              value={selectedActorTypeId}
            >
              <SelectTrigger className='w-full'>
                <SelectValue
                  placeholder={t(
                    'materialCommodityOrigins.cards.supplyChain.dialog.rolePlaceholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {actorTypes.map((actorType) => (
                  <SelectItem key={actorType.id} value={String(actorType.id)}>
                    {actorType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => handleOpenChange(false)}
            type='button'
            variant='outline'
          >
            {tCommon('cancel')}
          </Button>
          <Button
            disabled={
              isPending || !(selectedSubcontractorId && selectedActorTypeId)
            }
            onClick={handleSubmit}
            type='button'
            variant='declaration-primary'
          >
            {tCommon('add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
